import { create } from 'zustand';
import { temporal } from 'zundo';
import { arrayMove } from '@dnd-kit/sortable';
import type { FormSchema, FormField, FormStep, ConditionalRule, FormSettings } from '../types/form';

interface BuilderState {
  // Form data
  formId: string | null;
  formName: string;
  formSchema: FormSchema;
  originalSchema: FormSchema | null;

  // UI state
  selectedFieldId: string | null;
  selectedFieldIds: string[]; // For multi-select
  activeStepId: string | null;
  isDragging: boolean;
  hasUnsavedChanges: boolean;

  // Clipboard for copy/paste
  clipboard: FormField[] | null;

  // Actions
  initializeForm: (id: string, name: string, schema: FormSchema) => void;
  reset: () => void;

  // Field selection
  selectField: (fieldId: string | null) => void;

  // Multi-select operations
  toggleFieldSelection: (fieldId: string) => void;
  selectFieldRange: (fieldId: string) => void;
  selectAllFields: () => void;
  clearSelection: () => void;

  // Bulk operations
  removeSelectedFields: () => void;
  duplicateSelectedFields: () => void;
  moveSelectedFields: (direction: 'up' | 'down') => void;

  // Copy/paste
  copySelectedFields: () => void;
  pasteFields: () => void;

  // Field operations
  addField: (field: FormField, afterFieldId?: string) => void;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
  removeField: (fieldId: string) => void;
  reorderFields: (activeId: string, overId: string) => void;
  duplicateField: (fieldId: string) => void;
  moveFieldUp: (fieldId: string) => void;
  moveFieldDown: (fieldId: string) => void;

  // Step operations
  addStep: (step: FormStep) => void;
  updateStep: (stepId: string, updates: Partial<FormStep>) => void;
  removeStep: (stepId: string) => void;
  reorderSteps: (activeId: string, overId: string) => void;
  moveFieldToStep: (fieldId: string, stepId: string) => void;

  // Conditional logic
  addCondition: (rule: ConditionalRule) => void;
  updateCondition: (ruleId: string, updates: Partial<ConditionalRule>) => void;
  removeCondition: (ruleId: string) => void;
  updateConditionalLogic: (rules: ConditionalRule[]) => void;

  // Settings
  updateSettings: (settings: Partial<FormSettings>) => void;

  // Drag state
  setDragging: (isDragging: boolean) => void;

  // Get schema for saving
  getSchema: () => FormSchema;
  markAsSaved: () => void;
}

const initialSchema: FormSchema = {
  version: 1,
  fields: [],
  steps: [],
  settings: {
    submitButtonText: 'Submit',
    successMessage: 'Thank you for your submission!',
    theme: 'light',
    submitActions: [{ type: 'database' }],
  },
  conditionalLogic: [],
};

export const useBuilderStore = create<BuilderState>()(
  temporal(
    (set, get) => ({
  // Initial state
  formId: null,
  formName: '',
  formSchema: { ...initialSchema },
  originalSchema: null,
  selectedFieldId: null,
  selectedFieldIds: [],
  activeStepId: null,
  isDragging: false,
  hasUnsavedChanges: false,
  clipboard: null,

  initializeForm: (id, name, schema) =>
    set({
      formId: id,
      formName: name,
      formSchema: schema,
      originalSchema: JSON.parse(JSON.stringify(schema)),
      selectedFieldId: null,
      hasUnsavedChanges: false,
    }),

  reset: () =>
    set({
      formId: null,
      formName: '',
      formSchema: { ...initialSchema },
      originalSchema: null,
      selectedFieldId: null,
      selectedFieldIds: [],
      activeStepId: null,
      hasUnsavedChanges: false,
      clipboard: null,
    }),

  selectField: (fieldId) => set({ selectedFieldId: fieldId, selectedFieldIds: [] }),

  // Multi-select: toggle individual field (Ctrl+Click)
  toggleFieldSelection: (fieldId) =>
    set((state) => {
      const isSelected = state.selectedFieldIds.includes(fieldId);
      if (isSelected) {
        return {
          selectedFieldIds: state.selectedFieldIds.filter((id) => id !== fieldId),
          selectedFieldId: state.selectedFieldIds.length === 1 ? null : state.selectedFieldId,
        };
      } else {
        return {
          selectedFieldIds: [...state.selectedFieldIds, fieldId],
          selectedFieldId: fieldId,
        };
      }
    }),

  // Multi-select: select range (Shift+Click)
  selectFieldRange: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const lastSelected = state.selectedFieldId || state.selectedFieldIds[state.selectedFieldIds.length - 1];

      if (!lastSelected) {
        return { selectedFieldIds: [fieldId], selectedFieldId: fieldId };
      }

      const lastIndex = fields.findIndex((f) => f.id === lastSelected);
      const currentIndex = fields.findIndex((f) => f.id === fieldId);

      if (lastIndex === -1 || currentIndex === -1) {
        return { selectedFieldIds: [fieldId], selectedFieldId: fieldId };
      }

      const start = Math.min(lastIndex, currentIndex);
      const end = Math.max(lastIndex, currentIndex);
      const rangeIds = fields.slice(start, end + 1).map((f) => f.id);

      return {
        selectedFieldIds: [...new Set([...state.selectedFieldIds, ...rangeIds])],
        selectedFieldId: fieldId,
      };
    }),

  // Select all fields
  selectAllFields: () =>
    set((state) => ({
      selectedFieldIds: state.formSchema.fields.map((f) => f.id),
      selectedFieldId: state.formSchema.fields[0]?.id || null,
    })),

  // Clear multi-selection
  clearSelection: () => set({ selectedFieldIds: [], selectedFieldId: null }),

  // Remove all selected fields
  removeSelectedFields: () =>
    set((state) => {
      const idsToRemove = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToRemove.length === 0) return state;

      const fields = state.formSchema.fields.filter((f) => !idsToRemove.includes(f.id));
      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => !idsToRemove.includes(r.targetFieldId) &&
               !r.conditions.some((c) => idsToRemove.includes(c.fieldId))
      );

      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: null,
        selectedFieldIds: [],
        hasUnsavedChanges: true,
      };
    }),

  // Duplicate all selected fields
  duplicateSelectedFields: () =>
    set((state) => {
      const idsToDuplicate = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToDuplicate.length === 0) return state;

      const fields = [...state.formSchema.fields];
      const newFieldIds: string[] = [];

      // Find the last selected field to insert after
      const lastIndex = Math.max(
        ...idsToDuplicate.map((id) => fields.findIndex((f) => f.id === id))
      );

      // Create copies of selected fields in order
      const fieldsToDuplicate = idsToDuplicate
        .map((id) => fields.find((f) => f.id === id))
        .filter(Boolean) as FormField[];

      const newFields = fieldsToDuplicate.map((field) => {
        const newId = crypto.randomUUID();
        newFieldIds.push(newId);
        return {
          ...JSON.parse(JSON.stringify(field)),
          id: newId,
          name: `${field.name}_copy`,
          label: `${field.label} (Copy)`,
        };
      });

      // Insert after the last selected field
      fields.splice(lastIndex + 1, 0, ...newFields);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldIds: newFieldIds,
        selectedFieldId: newFieldIds[0],
        hasUnsavedChanges: true,
      };
    }),

  // Move selected fields up or down
  moveSelectedFields: (direction) =>
    set((state) => {
      const idsToMove = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToMove.length === 0) return state;

      const fields = [...state.formSchema.fields];
      const indices = idsToMove.map((id) => fields.findIndex((f) => f.id === id)).sort((a, b) => a - b);

      if (direction === 'up') {
        // Can't move up if first selected is at top
        if (indices[0] === 0) return state;

        for (const index of indices) {
          if (index > 0) {
            [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
          }
        }
      } else {
        // Can't move down if last selected is at bottom
        if (indices[indices.length - 1] === fields.length - 1) return state;

        for (let i = indices.length - 1; i >= 0; i--) {
          const index = indices[i];
          if (index < fields.length - 1) {
            [fields[index], fields[index + 1]] = [fields[index + 1], fields[index]];
          }
        }
      }

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  // Copy selected fields to clipboard (also to localStorage for cross-form paste)
  copySelectedFields: () =>
    set((state) => {
      const idsToCopy = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToCopy.length === 0) return state;

      const fieldsToCopy = idsToCopy
        .map((id) => state.formSchema.fields.find((f) => f.id === id))
        .filter(Boolean) as FormField[];

      const clipboardData = JSON.parse(JSON.stringify(fieldsToCopy));

      // Store in localStorage for cross-form paste
      try {
        localStorage.setItem('tfasim_field_clipboard', JSON.stringify({
          fields: clipboardData,
          timestamp: Date.now(),
        }));
      } catch (e) {
        // Ignore localStorage errors
      }

      return { clipboard: clipboardData };
    }),

  // Paste fields from clipboard (checks localStorage for cross-form paste)
  pasteFields: () =>
    set((state) => {
      // Try in-memory clipboard first, then localStorage
      let clipboardData = state.clipboard;

      if (!clipboardData || clipboardData.length === 0) {
        try {
          const stored = localStorage.getItem('tfasim_field_clipboard');
          if (stored) {
            const parsed = JSON.parse(stored);
            // Only use if less than 24 hours old
            if (parsed.fields && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
              clipboardData = parsed.fields;
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      if (!clipboardData || clipboardData.length === 0) return state;

      const fields = [...state.formSchema.fields];
      const newFieldIds: string[] = [];

      // Find insert position (after selected field or at end)
      let insertIndex = fields.length;
      if (state.selectedFieldId) {
        const idx = fields.findIndex((f) => f.id === state.selectedFieldId);
        if (idx !== -1) insertIndex = idx + 1;
      }

      // Create new fields with new IDs
      const newFields = clipboardData.map((field: FormField) => {
        const newId = crypto.randomUUID();
        newFieldIds.push(newId);
        return {
          ...JSON.parse(JSON.stringify(field)),
          id: newId,
          name: `${field.name}_${Date.now()}`,
        };
      });

      fields.splice(insertIndex, 0, ...newFields);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldIds: newFieldIds,
        selectedFieldId: newFieldIds[0],
        hasUnsavedChanges: true,
        clipboard: clipboardData, // Update in-memory clipboard
      };
    }),

  addField: (field, afterFieldId) =>
    set((state) => {
      const fields = [...state.formSchema.fields];
      if (afterFieldId) {
        const index = fields.findIndex((f) => f.id === afterFieldId);
        fields.splice(index + 1, 0, field);
      } else {
        fields.push(field);
      }
      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: field.id,
        hasUnsavedChanges: true,
      };
    }),

  updateField: (fieldId, updates) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) =>
        f.id === fieldId ? { ...f, ...updates } : f
      );
      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  removeField: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields.filter((f) => f.id !== fieldId);
      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => r.targetFieldId !== fieldId && !r.conditions.some((c) => c.fieldId === fieldId)
      );
      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: state.selectedFieldId === fieldId ? null : state.selectedFieldId,
        hasUnsavedChanges: true,
      };
    }),

  reorderFields: (activeId, overId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const activeIndex = fields.findIndex((f) => f.id === activeId);
      const overIndex = fields.findIndex((f) => f.id === overId);

      if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, fields: arrayMove(fields, activeIndex, overIndex) },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldUp: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const index = fields.findIndex((f) => f.id === fieldId);

      if (index <= 0) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, fields: arrayMove(fields, index, index - 1) },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldDown: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const index = fields.findIndex((f) => f.id === fieldId);

      if (index === -1 || index >= fields.length - 1) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, fields: arrayMove(fields, index, index + 1) },
        hasUnsavedChanges: true,
      };
    }),

  duplicateField: (fieldId) =>
    set((state) => {
      const field = state.formSchema.fields.find((f) => f.id === fieldId);
      if (!field) return state;

      const newField: FormField = {
        ...JSON.parse(JSON.stringify(field)),
        id: crypto.randomUUID(),
        name: `${field.name}_copy`,
        label: `${field.label} (Copy)`,
      };

      const fields = [...state.formSchema.fields];
      const index = fields.findIndex((f) => f.id === fieldId);
      fields.splice(index + 1, 0, newField);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: newField.id,
        hasUnsavedChanges: true,
      };
    }),

  addStep: (step) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: [...(state.formSchema.steps || []), step],
      },
      activeStepId: step.id,
      hasUnsavedChanges: true,
    })),

  updateStep: (stepId, updates) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: (state.formSchema.steps || []).map((s) =>
          s.id === stepId ? { ...s, ...updates } : s
        ),
      },
      hasUnsavedChanges: true,
    })),

  removeStep: (stepId) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: (state.formSchema.steps || []).filter((s) => s.id !== stepId),
      },
      activeStepId: state.activeStepId === stepId ? null : state.activeStepId,
      hasUnsavedChanges: true,
    })),

  reorderSteps: (activeId, overId) =>
    set((state) => {
      const steps = state.formSchema.steps || [];
      const activeIndex = steps.findIndex((s) => s.id === activeId);
      const overIndex = steps.findIndex((s) => s.id === overId);

      if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, steps: arrayMove(steps, activeIndex, overIndex) },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldToStep: (fieldId, stepId) =>
    set((state) => {
      const steps = (state.formSchema.steps || []).map((step) => ({
        ...step,
        fieldIds: step.fieldIds.filter((id) => id !== fieldId),
      }));

      const stepIndex = steps.findIndex((s) => s.id === stepId);
      if (stepIndex !== -1) {
        steps[stepIndex].fieldIds.push(fieldId);
      }

      return {
        formSchema: { ...state.formSchema, steps },
        hasUnsavedChanges: true,
      };
    }),

  addCondition: (rule) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: [...state.formSchema.conditionalLogic, rule],
      },
      hasUnsavedChanges: true,
    })),

  updateCondition: (ruleId, updates) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: state.formSchema.conditionalLogic.map((r) =>
          r.id === ruleId ? { ...r, ...updates } : r
        ),
      },
      hasUnsavedChanges: true,
    })),

  removeCondition: (ruleId) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: state.formSchema.conditionalLogic.filter((r) => r.id !== ruleId),
      },
      hasUnsavedChanges: true,
    })),

  updateConditionalLogic: (rules) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: rules,
      },
      hasUnsavedChanges: true,
    })),

  updateSettings: (settings) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        settings: { ...state.formSchema.settings, ...settings },
      },
      hasUnsavedChanges: true,
    })),

  setDragging: (isDragging) => set({ isDragging }),

  getSchema: () => get().formSchema,

  markAsSaved: () =>
    set((state) => ({
      originalSchema: JSON.parse(JSON.stringify(state.formSchema)),
      hasUnsavedChanges: false,
    })),
}),
    {
      // Only track changes to formSchema for undo/redo
      partialize: (state) => ({
        formSchema: state.formSchema,
      }),
      // Limit history to 50 items
      limit: 50,
      // Don't track rapid changes (debounce)
      handleSet: (handleSet) => {
        return (state) => {
          handleSet(state);
        };
      },
    }
  )
);
